import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const ICON_OPTIONS = [
    { value: "Hand", label: "Main" },
    { value: "Activity", label: "Activité" },
    { value: "Brain", label: "Cerveau" },
];

function Field({ label, hint, ...rest }) {
    return (
        <div>
            <label className="block text-xs font-semibold uppercase tracking-wider">{label}</label>
            <input className="mt-2 w-full rounded-xl border border-brand-line bg-white px-4 py-3 outline-none focus:border-brand-orange" {...rest} />
            {hint && <div className="mt-1 text-[11px] text-brand-smoke">{hint}</div>}
        </div>
    );
}

function TA({ label, hint, ...rest }) {
    return (
        <div>
            <label className="block text-xs font-semibold uppercase tracking-wider">{label}</label>
            <textarea rows={3} className="mt-2 w-full rounded-xl border border-brand-line bg-white px-4 py-3 outline-none focus:border-brand-orange" {...rest} />
            {hint && <div className="mt-1 text-[11px] text-brand-smoke">{hint}</div>}
        </div>
    );
}

function CursusDialog({ open, onClose, initial, onSaved }) {
    const isEdit = !!initial?.slug;
    const [f, setF] = useState(initial || {
        name: "", tagline: "", description: "", prevention_angle: "", icon: "Hand",
        image_url: "", color_accent: "#E8873C", order: 0,
    });
    useEffect(() => { setF(initial || { name: "", tagline: "", description: "", prevention_angle: "", icon: "Hand", image_url: "", color_accent: "#E8873C", order: 0 }); }, [initial, open]);
    const [saving, setSaving] = useState(false);
    if (!open) return null;

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (isEdit) await api.patch(`/admin/cursus/${initial.slug}`, f);
            else await api.post("/admin/cursus", f);
            toast.success(isEdit ? "Cursus mis à jour" : "Cursus créé");
            onSaved();
            onClose();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-ink/50 p-4" data-testid="cursus-dialog">
            <div className="relative w-full max-w-2xl rounded-3xl bg-white p-8 shadow-premium-lg">
                <button onClick={onClose} className="absolute right-4 top-4 text-brand-smoke hover:text-brand-ink"><X className="h-5 w-5" /></button>
                <h3 className="font-display text-3xl">{isEdit ? "Modifier le cursus" : "Nouveau cursus"}</h3>
                <form onSubmit={submit} className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Field label="Nom" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="cursus-field-name" />
                    <Field label="Tagline" value={f.tagline} onChange={(e) => setF({ ...f, tagline: e.target.value })} />
                    <div className="sm:col-span-2">
                        <TA label="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
                    </div>
                    <div className="sm:col-span-2">
                        <TA label="Angle de prévention" value={f.prevention_angle} onChange={(e) => setF({ ...f, prevention_angle: e.target.value })} />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider">Icône</label>
                        <select value={f.icon} onChange={(e) => setF({ ...f, icon: e.target.value })} className="mt-2 w-full rounded-xl border border-brand-line bg-white px-4 py-3">
                            {ICON_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </div>
                    <Field label="Ordre" type="number" value={f.order} onChange={(e) => setF({ ...f, order: parseInt(e.target.value || 0) })} />
                    <div className="sm:col-span-2">
                        <Field label="Image URL" value={f.image_url} onChange={(e) => setF({ ...f, image_url: e.target.value })} hint="URL Unsplash ou un asset personnalisé" />
                    </div>
                    <div className="flex justify-end gap-3 sm:col-span-2 pt-2">
                        <button type="button" onClick={onClose} className="rounded-full px-5 py-2.5 text-sm hover:bg-brand-line/50">Annuler</button>
                        <button type="submit" disabled={saving} data-testid="cursus-dialog-submit" className="rounded-full bg-brand-orange px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#d9772c] disabled:opacity-60">
                            {saving ? "Enregistrement…" : (isEdit ? "Enregistrer" : "Créer")}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default function AdminCursus() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialog, setDialog] = useState({ open: false, initial: null });

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/cursus");
            setItems(data);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const remove = async (c) => {
        if (!window.confirm(`Supprimer le cursus "${c.name}" ?`)) return;
        try {
            await api.delete(`/admin/cursus/${c.slug}`);
            toast.success("Cursus supprimé");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div className="mx-auto max-w-6xl px-6 py-10" data-testid="page-admin-cursus">
            <div className="flex items-end justify-between">
                <div>
                    <h1 className="font-display text-5xl font-normal tracking-tightest">Cursus</h1>
                    <p className="mt-2 text-brand-smoke">Gérez les parcours de formation.</p>
                </div>
                <button
                    onClick={() => setDialog({ open: true, initial: null })}
                    data-testid="admin-cursus-new"
                    className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-5 py-3 text-sm font-semibold text-white hover:bg-[#d9772c]"
                >
                    <Plus className="h-4 w-4" /> Nouveau cursus
                </button>
            </div>

            <div className="mt-8 overflow-hidden rounded-2xl border border-brand-line bg-white shadow-premium">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-brand-line bg-[#FAFAFA] text-left text-xs uppercase tracking-wider text-brand-smoke">
                            <th className="px-6 py-4">Nom</th>
                            <th className="px-6 py-4">Slug</th>
                            <th className="px-6 py-4">Tagline</th>
                            <th className="px-6 py-4">Ordre</th>
                            <th className="px-6 py-4 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-brand-line">
                        {loading ? (
                            <tr><td colSpan={5} className="px-6 py-10 text-center text-brand-smoke">Chargement…</td></tr>
                        ) : items.length === 0 ? (
                            <tr><td colSpan={5} className="px-6 py-10 text-center text-brand-smoke">Aucun cursus.</td></tr>
                        ) : (
                            items.map((c) => (
                                <tr key={c.slug} className="hover:bg-[#FAFAFA]" data-testid={`cursus-row-${c.slug}`}>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange"><BookOpen className="h-4 w-4" /></div>
                                            <strong className="text-brand-ink">{c.name}</strong>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 font-mono text-xs text-brand-smoke">{c.slug}</td>
                                    <td className="px-6 py-4 italic text-brand-ink/75">{c.tagline}</td>
                                    <td className="px-6 py-4 text-brand-smoke">{c.order}</td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="inline-flex gap-2">
                                            <button
                                                onClick={() => setDialog({ open: true, initial: c })}
                                                data-testid={`cursus-edit-${c.slug}`}
                                                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-brand-line hover:border-brand-orange hover:text-brand-orange"
                                            >
                                                <Pencil className="h-3.5 w-3.5" />
                                            </button>
                                            <button
                                                onClick={() => remove(c)}
                                                data-testid={`cursus-delete-${c.slug}`}
                                                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-brand-line hover:border-red-400 hover:text-red-600"
                                            >
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <CursusDialog
                open={dialog.open}
                onClose={() => setDialog({ open: false, initial: null })}
                initial={dialog.initial}
                onSaved={load}
            />
        </div>
    );
}
